import { Fragment, useLayoutEffect, useRef } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { AlertMessage, FitToViewport } from '@quizzach/components';
import { Colors, TextSize, Text, H3 } from '@quizzach/styles';
import { QuizIntroContainer } from '@quizzach/play-styles';
import { useEffect } from 'react';
import {
	ApiFailedReason,
	QuizOutletContextType,
	QuizMode,
} from '@quizzach/interfaces';
import {
	scrollToTop,
	handleDownloadTemplate,
	getHalftimeQuestion,
} from '@quizzach/utils';
import { DownloadIcon, IcArrowRight } from '@quizzach/assets';
import { useWindowListener } from '@quizzach/contexts';
import { fadeInConfig, Reveal } from '@quizzach/motion';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const MODE_PATTERNS: Record<QuizMode, Array<'q' | 'a'>> = {
	qaqa: ['q', 'a', 'q', 'a'],
	qqaa: ['q', 'q', 'a', 'a'],
};

const ModeSequence = ({ pattern }: { pattern: Array<'q' | 'a'> }) => {
	const { t, i18n } = useTranslation();
	const titleRef = useRef<HTMLHeadingElement>(null);

	// The sequence must stay on one line; shrink the font until it fits the
	// row instead of letting it wrap on narrow screens.
	useLayoutEffect(() => {
		const fit = () => {
			const el = titleRef.current;
			if (!el || !el.parentElement) return;
			el.style.fontSize = '';
			const available = el.parentElement.clientWidth;
			const needed = el.scrollWidth;
			if (needed > available && available > 0) {
				const base = parseFloat(getComputedStyle(el).fontSize);
				// 0.99: sub-pixel rounding must never push it back over the edge
				el.style.fontSize = `${(base * available * 0.99) / needed}px`;
			}
		};
		fit();
		document.fonts?.ready.then(fit);
		// Refitting mutates the h3's own size, so observe the row width via
		// the parent — otherwise the observer would loop on its own writes.
		const observer = new ResizeObserver(fit);
		if (titleRef.current?.parentElement)
			observer.observe(titleRef.current.parentElement);
		window.addEventListener('resize', fit);
		return () => {
			observer.disconnect();
			window.removeEventListener('resize', fit);
		};
	}, [i18n.language, pattern]);

	return (
		<h3 ref={titleRef}>
			{pattern.map((step, index) => (
				<Fragment key={index}>
					{index > 0 && <span className='sep'>-</span>}
					<span className={step === 'a' ? 'ans' : undefined}>
						{t(step === 'a' ? 'quiz.mode.answer' : 'quiz.mode.question')}
					</span>
				</Fragment>
			))}
		</h3>
	);
};

const DownloadTemplate = ({
	touchDevice,
	handleDownloadTemplate,
}: {
	touchDevice: boolean;
	handleDownloadTemplate: VoidFunction;
}) => {
	const { t } = useTranslation();
	return (
		<motion.div
			className='flex'
			style={{
				alignItems: 'center',
				cursor: 'pointer',
			}}
			onClick={handleDownloadTemplate}
			{...fadeInConfig()}
		>
			<DownloadIcon color={Colors.textBrand} size={touchDevice ? 16 : 25} />
			<Text
				color={Colors.textBrand}
				style={{ textAlign: 'left', margin: '0px 0px 0px 10px' }}
				$size={TextSize.s}
			>
				{t('quiz.modeSelection.downloadTemplate')}
			</Text>
		</motion.div>
	);
};

export const QuizModeSelection = () => {
	const { quiz, quizType, halftime, error, handleSetHalftime, handleSetMode } =
		useOutletContext<QuizOutletContextType>();

	const { t } = useTranslation();
	const { uuid } = useParams<{ uuid: string }>();
	const navigate = useNavigate();
	const { touchDevice } = useWindowListener();

	const handleChooseMode = (mode: QuizMode) => {
		handleSetMode(mode);
		navigate(`/quiz/${uuid}/start`);
	};

	useEffect(() => {
		scrollToTop();
	}, []);

	if (!quiz) return;

	const hasChapters =
		quiz?.premade_quiz?.chapters && quiz.premade_quiz.chapters.length > 0;
	// Halftime is offered (also for chaptered quizzes) whenever the quiz can be
	// split into halves; never for puzzles.
	const canHalftime =
		!quiz?.premade_quiz?.puzzle && getHalftimeQuestion(quiz) !== null;
	const qaqaDisabled = Boolean(quiz?.premade_quiz?.puzzle || hasChapters);
	const qaqaDisabledNote = quiz?.premade_quiz?.puzzle
		? t('quiz.modeSelection.disabledPuzzle')
		: t('quiz.modeSelection.disabledChapters');

	const content = (
		<>
			<Reveal className='quiz-mode-titlewrap'>
				<H3 className='quiz-mode-title' color={Colors.textMain}>
					{t('quiz.modeSelection.title')}
				</H3>
			</Reveal>
			{!error && (
				<div className='quiz-mode-rows quiz-play-rows'>
					<Reveal
						className={`quiz-mode-row${qaqaDisabled ? ' disabled' : ''}`}
						onClick={qaqaDisabled ? undefined : () => handleChooseMode('qaqa')}
					>
						<div className='in'>
							<span className='num'>01</span>
							<div className='main'>
								<ModeSequence pattern={MODE_PATTERNS.qaqa} />
							</div>
							<span className={`desc${qaqaDisabled ? ' note' : ''}`}>
								{qaqaDisabled
									? qaqaDisabledNote
									: t('quiz.modeSelection.qaqaDescription')}
							</span>
							<span className='arr'>
								<IcArrowRight size={20} />
							</span>
						</div>
					</Reveal>

					<Reveal
						className='quiz-mode-row'
						onClick={() => handleChooseMode('qqaa')}
					>
						<div className='in'>
							<span className='num'>02</span>
							<div className='main'>
								<ModeSequence pattern={MODE_PATTERNS.qqaa} />
								{canHalftime && (
									<div
										className='quiz-mode-halftime'
										onClick={(event) => {
											event.stopPropagation();
											handleSetHalftime(!halftime);
										}}
									>
										<span
											className={`quiz-mode-switch${halftime ? ' on' : ''}`}
											role='switch'
											aria-checked={halftime}
										/>
										<span className='quiz-mode-halftime-label'>
											{t('quiz.modeSelection.halftimeCheckbox')}
										</span>
									</div>
								)}
							</div>
							<span className='desc'>
								{t('quiz.modeSelection.qqaaDescription')}
							</span>
							<span className='arr'>
								<IcArrowRight size={20} />
							</span>
						</div>
					</Reveal>
				</div>
			)}
			{!error &&
				quizType === 'premade' &&
				quiz?.premade_quiz?.answers_template && (
					<Reveal delay={1} className='quiz-mode-template'>
						<DownloadTemplate
							touchDevice={touchDevice}
							handleDownloadTemplate={() => handleDownloadTemplate(quiz)}
						/>
					</Reveal>
				)}
		</>
	);

	return (
		<QuizIntroContainer
			data-id='quiz-mode-selection-container-00'
			$noBottomBar={touchDevice}
		>
			{error && (
				<AlertMessage
					code={ApiFailedReason.default}
					type='error'
					style={{ margin: '0px auto' }}
				/>
			)}
			{/* Mobile scrolls naturally; the fit-to-viewport scaling is a
			    desktop/presentation concern. The spacers seat the rows in the
			    lower third of the stage, same as the start screen. */}
			{touchDevice ? (
				<>
					<span className='quiz-start-space' aria-hidden='true' />
					{content}
					<span className='quiz-start-space bottom' aria-hidden='true' />
				</>
			) : (
				<FitToViewport topAligned>{content}</FitToViewport>
			)}
		</QuizIntroContainer>
	);
};
